import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import CompaniaArcaPanel from './CompaniaArcaPanel';
import companiaService, { Compania } from '../../services/companiaService';
import wsfeConfigService, { WsfeConfigItem } from '../../services/wsfeConfigService';

const CertificadosArca: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [idCompania, setIdCompania] = useState<string>('');
  const [huerfanas, setHuerfanas] = useState<WsfeConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editarHuerfanaId, setEditarHuerfanaId] = useState<string | null>(null);

  const selected = useMemo(
    () => companias.find((c) => c.idCompania.trim() === idCompania.trim()) ?? null,
    [companias, idCompania]
  );

  const cargar = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      const [listaCompanias, configs] = await Promise.all([
        companiaService.getCompanias({ pageNumber: 1, pageSize: 200 }),
        wsfeConfigService.getWsfeConfigs(),
      ]);
      const companiasOk = Array.isArray(listaCompanias) ? listaCompanias : [];
      setCompanias(companiasOk);

      const ids = new Set(companiasOk.map((c) => (c.idCompania ?? '').trim()).filter(Boolean));
      const sinCompania = configs.filter((c) => {
        const id = (c.idCompania ?? '').trim();
        return !id || !ids.has(id);
      });
      setHuerfanas(sinCompania);

      setIdCompania((prev) => {
        if (prev && ids.has(prev.trim())) return prev;
        return companiasOk[0]?.idCompania ?? '';
      });
    } catch {
      enqueueSnackbar('Error al cargar compañías y certificados ARCA', { variant: 'error' });
      if (!silent) {
        setCompanias([]);
        setHuerfanas([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const handleEliminarHuerfana = async (idwsfe: string) => {
    if (!window.confirm(`¿Eliminar la configuración "${idwsfe}"?`)) return;
    try {
      await wsfeConfigService.deleteWsfeConfig(idwsfe);
      enqueueSnackbar('Configuración eliminada', { variant: 'success' });
      await cargar();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al eliminar', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Certificados ARCA
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configurá certificados, claves privadas y servicios web WSFE por compañía.
            </Typography>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <SearchableSelect
                label="Compañía"
                value={idCompania || null}
                onChange={(val) => setIdCompania(val ?? '')}
                options={companias.map((c) => ({
                  value: c.idCompania,
                  label: `${c.idCompania} — ${c.nombre}${c.cuit ? ` (${c.cuit})` : ''}`,
                }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, maxWidth: 560 } }}
              />

              {selected ? (
                <CompaniaArcaPanel
                  key={selected.idCompania}
                  idCompania={selected.idCompania}
                  cuitCompania={selected.cuit}
                  companias={companias}
                  editarIdwsfe={editarHuerfanaId}
                  onEditarIdwsfeConsumed={() => setEditarHuerfanaId(null)}
                  onSaved={(idCia) => {
                    if (idCia.trim()) setIdCompania(idCia.trim());
                    void cargar({ silent: true });
                  }}
                />
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No hay compañías cargadas. Creá una compañía para asociar certificados ARCA.
                </Alert>
              )}

              {huerfanas.length > 0 && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Hay configuraciones WSFE sin compañía válida. Editálas para asignarlas a una compañía, o
                    eliminalas para evitar consultas a certificados que no se usan (por ejemplo homologación).
                  </Alert>
                  <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Id WSFE</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Compañía</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Tipo WS</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {huerfanas.map((item) => (
                          <TableRow
                            key={item.idwsfe}
                            hover
                            onDoubleClick={() => selected && setEditarHuerfanaId(item.idwsfe)}
                            sx={{ cursor: selected ? 'pointer' : 'default' }}
                          >
                            <TableCell>{item.idwsfe}</TableCell>
                            <TableCell>
                              {(item.idCompania ?? '').trim() ? (
                                item.idCompania
                              ) : (
                                <Chip size="small" label="Sin compañía" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>{item.tipows}</TableCell>
                            <TableCell>{item.descripcion}</TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip title="Modificar">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={!selected}
                                      onClick={() => setEditarHuerfanaId(item.idwsfe)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleEliminarHuerfana(item.idwsfe)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CertificadosArca;
